/*
 * SUPABASE MIGRATION — run in the Supabase SQL editor before using this component
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * CREATE TABLE IF NOT EXISTS onboarding_progress (
 *   id           uuid        DEFAULT gen_random_uuid() PRIMARY KEY,
 *   user_id      uuid        NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
 *   step_key     text        NOT NULL,
 *   completed_at timestamptz DEFAULT now(),
 *   UNIQUE(user_id, step_key)
 * );
 *
 * ALTER TABLE onboarding_progress ENABLE ROW LEVEL SECURITY;
 *
 * CREATE POLICY "Users manage own onboarding progress"
 *   ON onboarding_progress
 *   FOR ALL
 *   USING (auth.uid() = user_id)
 *   WITH CHECK (auth.uid() = user_id);
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';

// ── Step definitions ──────────────────────────────────────────────────────────

const STEPS = [
  {
    key: 'profile',
    title: 'Complete your company profile',
    description: 'Add your CAGE code, system name, and primary contact so your SSP is ready to submit.',
    cta: 'Go to Profile',
    view: 'companySettings',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
  {
    key: 'framework',
    title: 'Choose your compliance framework',
    description: 'Select CMMC Level 2, NIST SP 800-171, or another framework that matches your contract requirements.',
    cta: 'Choose Framework',
    view: 'frameworks',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    key: 'controls',
    title: 'Answer your first 10 controls',
    description: 'Work through each control and mark it Yes, Partial, or No. Your readiness score updates in real time.',
    cta: 'Start Assessment',
    view: 'assessment',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4"/>
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    ),
  },
  {
    key: 'evidence',
    title: 'Upload your first piece of evidence',
    description: 'Attach a policy document, configuration screenshot, or audit record to back up a control answer.',
    cta: 'Go to Evidence Locker',
    view: 'evidence',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.34a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
      </svg>
    ),
  },
  {
    key: 'ai',
    title: 'Run your first AI gap analysis',
    description: 'Ask the TheClearAudit Assistant to analyze a specific control and get actionable remediation suggestions.',
    cta: 'Open AI Assistant',
    view: 'ai',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
  },
  {
    key: 'ssp_export',
    title: 'Export your System Security Plan',
    description: 'Download your completed SSP as a Word document or spreadsheet — ready to share with your C3PAO.',
    cta: 'Export SSP',
    view: 'report',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="12" y2="18"/><line x1="15" y1="15" x2="12" y2="18"/>
      </svg>
    ),
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function OnboardingChecklist({ session, setView }) {
  const [completedKeys, setCompletedKeys] = useState(new Set());
  const [dismissed, setDismissed]         = useState(false);
  const [loading, setLoading]             = useState(true);
  const [showCongrats, setShowCongrats]   = useState(false);

  const userId = session?.user?.id;

  // Compute allDone here so the useEffect below can reference it
  const allDone = STEPS.every(s => completedKeys.has(s.key));

  // Load progress from Supabase
  const loadProgress = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from('onboarding_progress')
      .select('step_key')
      .eq('user_id', userId);
    if (!error && data) {
      const keys = new Set(data.map(r => r.step_key));
      // Support both old 'dismissed' key and new 'checklist_dismissed'
      if (keys.has('dismissed') || keys.has('checklist_dismissed')) { setDismissed(true); }
      setCompletedKeys(keys);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  // Mark a step complete in Supabase
  const markComplete = useCallback(async (stepKey) => {
    if (!userId || completedKeys.has(stepKey)) return;
    const { error } = await supabase
      .from('onboarding_progress')
      .upsert({ user_id: userId, step_key: stepKey }, { onConflict: 'user_id,step_key' });
    if (!error) {
      setCompletedKeys(prev => new Set([...prev, stepKey]));
    }
  }, [userId, completedKeys]);

  // Dismiss permanently
  const handleDismiss = useCallback(async () => {
    if (!userId) { setDismissed(true); return; }
    await supabase
      .from('onboarding_progress')
      .upsert({ user_id: userId, step_key: 'checklist_dismissed' }, { onConflict: 'user_id,step_key' });
    setDismissed(true);
  }, [userId]);

  // Auto-dismiss after 3s congrats when all steps are done
  useEffect(() => {
    if (loading || dismissed || !allDone) return;
    setShowCongrats(true);
    const timer = setTimeout(() => { handleDismiss(); }, 3000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone, loading, dismissed]);

  // CTA: mark step complete then navigate
  const handleCta = useCallback((step) => {
    markComplete(step.key);
    if (setView) setView(step.view);
  }, [markComplete, setView]);

  // Toggle step completion manually via checkbox
  const handleToggle = useCallback(async (stepKey) => {
    if (!userId) return;
    if (completedKeys.has(stepKey)) {
      // Unmark
      const { error } = await supabase
        .from('onboarding_progress')
        .delete()
        .eq('user_id', userId)
        .eq('step_key', stepKey);
      if (!error) {
        setCompletedKeys(prev => { const next = new Set(prev); next.delete(stepKey); return next; });
      }
    } else {
      markComplete(stepKey);
    }
  }, [userId, completedKeys, markComplete]);

  if (loading || dismissed) return null;

  const stepCount = STEPS.length;
  const doneCount = STEPS.filter(s => completedKeys.has(s.key)).length;
  const pct       = Math.round((doneCount / stepCount) * 100);

  return (
    <div
      className="glass-card"
      style={{
        marginBottom: 20,
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid var(--border2)',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: allDone ? 'var(--green-bg)' : 'var(--blue-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: allDone ? 'var(--green)' : 'var(--accent)',
            flexShrink: 0,
          }}>
            {allDone ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            )}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>
              {allDone ? 'Setup complete — you\'re ready to go!' : 'Your compliance setup checklist'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
              {allDone ? 'All steps completed.' : `${doneCount} of ${stepCount} — complete these before your C3PAO assessment`}
            </div>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          title="Dismiss"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text3)', padding: 4, display: 'flex',
            alignItems: 'center', borderRadius: 4,
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--text2)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ padding: '10px 20px 0', background: 'var(--bg2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            flex: 1, height: 6, borderRadius: 99,
            background: 'var(--border2)', overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${pct}%`,
              borderRadius: 99,
              background: allDone
                ? 'var(--green)'
                : 'linear-gradient(90deg, var(--accent), #5B9BEC)',
              transition: 'width 0.4s ease',
            }}/>
          </div>
          <span style={{ fontSize: 10, color: 'var(--text3)', flexShrink: 0, fontFamily: 'var(--mono)' }}>
            {pct}%
          </span>
        </div>
      </div>

      {/* Congratulations state — auto-dismisses after 3 s */}
      {showCongrats ? (
        <div style={{
          padding: '28px 20px',
          background: 'var(--bg2)',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 12, textAlign: 'center',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 99,
            background: 'var(--green-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--green)',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
              You're all set! Your compliance journey has started.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.55 }}>
              This checklist will close in a moment.
            </div>
          </div>
        </div>
      ) : (
        /* Step list */
        <div style={{ background: 'var(--bg2)' }}>
          {STEPS.map((step, idx) => {
            const done = completedKeys.has(step.key);
            const isLast = idx === STEPS.length - 1;
            return (
              <div
                key={step.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 20px',
                  borderBottom: isLast ? 'none' : '1px solid var(--border)',
                  opacity: done ? 0.6 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                {/* Checkbox */}
                <button
                  onClick={() => handleToggle(step.key)}
                  title={done ? 'Mark incomplete' : 'Mark complete'}
                  style={{
                    flexShrink: 0,
                    marginTop: 1,
                    width: 18, height: 18,
                    borderRadius: 5,
                    border: done ? 'none' : '1.5px solid var(--border2)',
                    background: done ? 'var(--green)' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0,
                    transition: 'background 0.2s, border 0.2s',
                  }}
                >
                  {done && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>

                {/* Step icon */}
                <div style={{
                  flexShrink: 0,
                  marginTop: 1,
                  width: 28, height: 28,
                  borderRadius: 7,
                  background: done ? 'var(--green-bg)' : 'var(--surface)',
                  border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: done ? 'var(--green)' : 'var(--text3)',
                  transition: 'all 0.2s',
                }}>
                  {step.icon}
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: done ? 'var(--text3)' : 'var(--text)',
                    textDecoration: done ? 'line-through' : 'none',
                    lineHeight: 1.4,
                    marginBottom: 2,
                  }}>
                    {step.title}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                    {step.description}
                  </div>
                </div>

                {/* CTA */}
                {!done && (
                  <button
                    onClick={() => handleCta(step)}
                    style={{
                      flexShrink: 0,
                      fontSize: 11,
                      fontWeight: 500,
                      fontFamily: 'var(--font)',
                      padding: '5px 12px',
                      borderRadius: 6,
                      border: '1px solid var(--accent)',
                      background: 'transparent',
                      color: 'var(--accent)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'var(--accent)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--accent)';
                    }}
                  >
                    {step.cta}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
